import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { EVENT_PRICES as P } from "./eventPricing";

const grandPrixDeMonaco: ChauffeurPage = {
  slug: "grand-prix-de-monaco",
  title: "Chauffeur privé Grand Prix de Monaco – VTC F1",
  subtitle:
    "Chauffeur privé pendant le Grand Prix de Monaco : timing avant fermetures du circuit, liaisons Nice ↔ Monaco et coordination héliport.",
  keywords: [
    "chauffeur grand prix de monaco",
    "vtc grand prix monaco f1",
    "chauffeur privé grand prix monaco",
  ],
  hero: {
    title: "Chauffeur privé pendant le Grand Prix de Monaco",
    subtitle:
      "Fin mai, le circuit en ville ferme des artères entières : un chauffeur qui maîtrise les fenêtres horaires et les contournements.",
    keywords: [
      "chauffeur Grand Prix de Monaco",
      "VTC F1 Monaco",
      "chauffeur paddock Port Hercule",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant le Grand Prix de Monaco",
    badges: [
      "Timing avant fermetures",
      "Liaison Nice ↔ Monaco",
      "Coordination héliport",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur pendant le Grand Prix",
    items: [
      {
        title: "Maîtrise des fermetures",
        description:
          "Le circuit urbain ferme des artères plusieurs jours : le chauffeur connaît les fenêtres d'accès et les déposes possibles avant blocage.",
      },
      {
        title: "Liaison Nice ↔ Monaco",
        description:
          "Accès à la Principauté saturé pendant le week-end : itinéraires anticipés par corniches ou A8 selon les coupures.",
      },
      {
        title: "Paddock & yachts",
        description:
          "Hospitalités paddock, terrasses et yachts amarrés à Port Hercule : dépose au plus près malgré les barrières.",
      },
      {
        title: "Clientèle ultra-VIP",
        description:
          "Dirigeants, écuries et invités prestige : discrétion absolue, chauffeur en tenue et ponctualité sur agendas serrés.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant le Grand Prix de Monaco",
    services: [
      {
        title: "Liaison aéroport de Nice",
        text: "Transfert Nice ↔ Monaco avec suivi de vol et coordination possible avec l'héliport de Monaco.",
      },
      {
        title: "Timing autour des fermetures",
        text: "Départs et déposes calés sur les fenêtres horaires d'ouverture des voies du circuit.",
      },
      {
        title: "Accès Port Hercule",
        text: "Dépose et reprise au plus près des yachts et hospitalités amarrés sur le port, malgré les barrières.",
      },
      {
        title: "Mise à disposition week-end",
        text: "Chauffeur dédié sur les journées d'essais, qualifications et course pour enchaîner sans temps mort.",
      },
      {
        title: "Soirées & galas",
        text: "After-races, dîners et soirées en Principauté ou à Cannes, retour de nuit assuré.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant le Grand Prix",
    items: [
      {
        name: "Circuit urbain de Monaco",
        description:
          "Sainte-Dévote, la montée du Casino, le tunnel, la chicane du port.",
      },
      {
        name: "Port Hercule",
        description:
          "Yachts d'hospitalité, terrasses et zone des grandes réceptions.",
      },
      {
        name: "Monte-Carlo & Casino",
        description: "Palaces, paddock club et soirées de prestige.",
      },
      {
        name: "Héliport de Monaco (Fontvieille)",
        description: "Liaisons rapides avec Nice pendant les pics d'affluence.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description:
          "Terminaux 1 et 2, arrivées concentrées sur le week-end de course.",
      },
    ],
  },
  generic: {
    heading: "Le Grand Prix de Monaco vu d'un chauffeur",
    subheading:
      "Fin mai, un circuit tracé dans la ville : la voirie ferme par phases et chaque déplacement se planifie à l'avance.",
    paragraphs: [
      "Chaque fin mai, le Grand Prix de Monaco transforme la Principauté en circuit urbain. De Sainte-Dévote à la chicane du port, des artères entières ferment plusieurs jours et l'accès aux quais se fait par fenêtres horaires. Un chauffeur qui maîtrise ces coupures dépose au plus près avant blocage et anticipe les contournements.",
      "L'accès à Monaco sature pendant le week-end. Nous anticipons les liaisons Nice ↔ Monaco par les corniches ou l'A8 selon les coupures, et coordonnons si besoin avec l'héliport de Fontvieille pour franchir les pics d'affluence.",
      "La clientèle du Grand Prix — écuries, partenaires, invités prestige — enchaîne hospitalités paddock, yachts de Port Hercule et soirées. La mise à disposition sur les journées d'essais, qualifications et course garde le même chauffeur et assure les retours de nuit après les after-races.",
    ],
    bullets: [
      "Déposes avant blocage",
      "Corniches ou A8 selon coupures",
      "Yacht-to-circuit Port Hercule",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour le Grand Prix de Monaco",
    steps: [
      "Précisez vos journées (essais, qualifications, course), votre hébergement et vos accès paddock/yachts.",
      "Nous calons les départs sur les fenêtres d'ouverture des voies et les liaisons Nice ↔ Monaco.",
      "Le chauffeur se présente en tenue, avec suivi de vol et coordination héliport si besoin.",
      "Mise à disposition week-end pour enchaîner circuit, hospitalités et soirées.",
    ],
  },
  faq: [
    {
      q: "Combien coûte un chauffeur privé pendant le Grand Prix de Monaco ?",
      a: `Les liaisons vers Monaco sont à forfait : depuis l'aéroport de Nice, ${P.monacoNiceE} € en Classe E, ${P.monacoNiceV} € en Classe V (7 places) et ${P.monacoNiceS} € en Classe S ; depuis Cannes, ${P.cannesMonacoE} €, ${P.cannesMonacoV} € ou ${P.cannesMonacoS} €. Pour garder le chauffeur toute la journée, la mise à disposition démarre à ${P.e3} € les 3 h en Classe E, et une journée de 8 h coûte ${P.e8} €, ${P.v8} € ou ${P.s8} € selon la classe. Ce sont nos tarifs publics, et le prix final est confirmé par écrit avant la prestation.`,
    },
    {
      q: "Transfert ou mise à disposition : que choisir pour le Grand Prix ?",
      a: `Le transfert suffit si vous allez à Monaco le matin et en repartez le soir : deux trajets à prix fixe. La mise à disposition garde le même chauffeur sur place quand les fermetures du circuit rendent chaque nouveau trajet incertain, et pour enchaîner tribune, yacht et dîner. Elle se prend à partir de 3 h.`,
    },
    {
      q: "Comment réserver un chauffeur pendant le Grand Prix de Monaco ?",
      a: "Réservez très en amont : c'est l'un des week-ends les plus tendus de la Riviera. Indiquez vos journées, votre hébergement et vos accès (paddock, yachts) pour que nous bloquions un chauffeur dédié et planifions les déplacements autour des fermetures.",
    },
    {
      q: "Comment gérez-vous les fermetures de routes du circuit ?",
      a: "Le circuit urbain ferme des artères par phases, plusieurs jours. Nous calons les départs et déposes sur les fenêtres horaires d'ouverture des voies et anticipons les contournements pour vous rapprocher au maximum avant blocage.",
    },
    {
      q: "La liaison entre l'aéroport de Nice et Monaco reste-t-elle possible pendant la course ?",
      a: "Oui, mais l'accès sature. Nous anticipons l'itinéraire par les corniches ou l'A8 selon les coupures, avec suivi de vol, et pouvons coordonner avec l'héliport de Monaco pour franchir les pics d'affluence.",
    },
    {
      q: "Pouvez-vous déposer au plus près des yachts de Port Hercule ?",
      a: "Oui, dans la limite des barrières et périmètres de sécurité. Le chauffeur dépose au point autorisé le plus proche des yachts et hospitalités, puis vous reprend à la sortie selon les fenêtres d'accès.",
    },
  ],
  relatedLinks: [
    {
      label: "Trajet Monaco Aéroport de Nice en voiture avec chauffeur",
      href: "/chauffeur/transferts/monaco-aeroport-nice",
    },
    {
      label: "Trajet Cannes Monaco en voiture avec chauffeur",
      href: "/chauffeur/transferts/cannes-monaco",
    },
    {
      label: "Faire une demande de réservation",
      href: "/reservation",
    },
  ],
  cta: {
    title: "Votre chauffeur pour le Grand Prix de Monaco",
    text: "Timing autour des fermetures, liaisons Nice ↔ Monaco et héliport : réservez un chauffeur dédié pour le week-end.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    // MESURE 2026-10-01 : 238 affichages en 90 j, position 30 (13,5 sur
    // « chauffeur vtc grand prix de monaco »), aucun pack local.
    title: `Chauffeur privé Grand Prix de Monaco, dès ${P.monacoNiceE} € | KS Rent Services`,
    description: `Grand Prix de Monaco : aéroport de Nice ⇄ Monaco dès ${P.monacoNiceE} €, Cannes ⇄ Monaco dès ${P.cannesMonacoE} €, chauffeur à disposition dès ${P.e3} € les 3 h. Prix fixés à l'avance.`,
    canonical: "/chauffeur/grand-prix-de-monaco",
  },
};

export default grandPrixDeMonaco;
